import { useState, useEffect, useCallback } from "react";
import { IPreConversation } from "src/domains";
import {
  queryGetPreConversations,
  queryGetPreConversation,
  queryGetPreConversationByService,
  queryGetPreConversationsUnreadCount,
  mutationPreConversationCreate,
  IMutationPreConversationCreateRequest,
} from "src/graphql";
import { AppState, AppStateStatus } from "react-native";
import {
  ChatMessageEvent,
  TabChangeEvent,
  LogoutEvent,
  RequestUnreadCountRefreshEvent,
} from "src/utils/event";
import { EventRegister } from "react-native-event-listeners";
import { atom, useRecoilState } from "recoil";
import { useAuth, AuthState } from "src/hooks/cognito";

export const preOfferChatUnreadCountState = atom<number>({
  key: "preOfferChatUnreadCountState",
  default: 0,
});

export const usePreOfferHistory = () => {
  const [items, setItems] = useState<IPreConversation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    if (_isMount) {
      setError(undefined);
    }
    try {
      const response = await queryGetPreConversations({});
      if (_isMount) {
        setItems(
          response.data.getPreConversations?.items?.sort((a, b) => {
            if (
              a.lastMessage?.createdAt &&
              b.lastMessage?.createdAt &&
              a.lastMessage?.createdAt < b.lastMessage?.createdAt
            ) {
              return 1;
            }
            if (
              a.lastMessage?.createdAt &&
              b.lastMessage?.createdAt &&
              a.id > b.id
            ) {
              return -1;
            }
            return 0;
          }) || []
        );
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, []);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    const tabChangeListener = EventRegister.addEventListener(
      TabChangeEvent,
      (e) => {
        if (e.tabName === "PreOfferChatList") {
          refresh();
        }
      }
    );
    const chatMessageListener = EventRegister.addEventListener(
      ChatMessageEvent,
      (e) => {
        refresh();
      }
    );
    return () => {
      EventRegister.removeEventListener(tabChangeListener as string);
      EventRegister.removeEventListener(chatMessageListener as string);
    };
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const logoutListener = EventRegister.addEventListener(LogoutEvent, () => {
      setItems([]);
      setError(undefined);
    });
    return () => {
      EventRegister.removeEventListener(logoutListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [items, loading, error, refresh] as const;
};

export const usePreOffer = (id?: string) => {
  const [item, setItem] = useState<IPreConversation | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!id || !_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    if (_isMount) {
      setError(undefined);
    }
    try {
      const response = await queryGetPreConversation({ id });
      if (_isMount) {
        setItem(response.data.getPreConversation);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    refresh();
  }, [refresh, id]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [item, loading, error, refresh] as const;
};

export const usePreOfferByService = (serviceId: string) => {
  const auth = useAuth();
  const [item, setItem] = useState<IPreConversation | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (auth !== AuthState.Auth) {
      // Skip for unath
      return;
    }
    if (!_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    if (_isMount) {
      setError(undefined);
    }
    try {
      const response = await queryGetPreConversationByService({ serviceId });
      if (_isMount) {
        setItem(response.data.getPreConversationByService);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [serviceId, auth]);

  useEffect(() => {
    refresh();
  }, [auth, refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [item, loading, error, refresh] as const;
};

export const usePreOfferActions = () => {
  const createPreOffer = useCallback(
    async (params: IMutationPreConversationCreateRequest) => {
      await mutationPreConversationCreate(params);
    },
    []
  );

  return { createPreOffer } as const;
};

export const usePreOfferUnreadCount = () => {
  const auth = useAuth();
  const [count, setCount] = useRecoilState<number>(
    preOfferChatUnreadCountState
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (auth !== AuthState.Auth) {
      // Skip on unath
      return;
    }
    if (!_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    if (_isMount) {
      setError(undefined);
    }
    try {
      const response = await queryGetPreConversationsUnreadCount();
      if (_isMount) {
        setCount(response.data.getPreConversationsUnreadCount || 0);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [auth]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    const tabChangeListener = EventRegister.addEventListener(
      TabChangeEvent,
      (e) => {
        refresh();
      }
    );
    const chatMessageListener = EventRegister.addEventListener(
      ChatMessageEvent,
      (e) => {
        refresh();
      }
    );
    const requestUnreadCountRefreshListener = EventRegister.addEventListener(
      RequestUnreadCountRefreshEvent,
      (e) => {
        refresh();
      }
    );
    return () => {
      EventRegister.removeEventListener(tabChangeListener as string);
      EventRegister.removeEventListener(chatMessageListener as string);
      EventRegister.removeEventListener(
        requestUnreadCountRefreshListener as string
      );
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh, auth]);

  useEffect(() => {
    const logoutListener = EventRegister.addEventListener(LogoutEvent, () => {
      setCount(0);
      setError(undefined);
    });
    return () => {
      EventRegister.removeEventListener(logoutListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [count, loading, error, refresh] as const;
};
